import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useEffect, useRef, useState } from 'react';
import { AccessibilityInfo, Platform } from 'react-native';

import { Text } from '@/components/ui/text';

/**
 * The words a screen reader hears as a searchable list changes: "Loading results" when a new
 * query is applied, then the count once it lands. `queryKey` changes only when the debounced
 * query is applied, so typing announces nothing until then. Nothing is said for the first load,
 * which the busy list already covers, or when the request fails, which the failure state
 * announces itself.
 *
 * A list that loads in pages passes `hasMore`, so the count reads "24 or more results" rather
 * than claiming that the first page is everything.
 */
export function useResultsAnnouncement({
  queryKey,
  isSettled,
  isError,
  resultCount,
  hasMore = false,
}: {
  queryKey: string;
  /** The posts on screen answer `queryKey`, not an earlier query. */
  isSettled: boolean;
  isError: boolean;
  resultCount: number;
  hasMore?: boolean;
}) {
  const [message, setMessage] = useState('');

  const lastKeyRef = useRef(queryKey);

  const isAwaitingRef = useRef(false);

  useEffect(() => {
    if (lastKeyRef.current !== queryKey) {
      lastKeyRef.current = queryKey;

      isAwaitingRef.current = true;

      if (!isSettled || isError) {
        setMessage(isError ? '' : 'Loading results');

        if (isError) {
          isAwaitingRef.current = false;
        }

        return;
      }
    }

    if (!isAwaitingRef.current) {
      return;
    }

    if (isError) {
      isAwaitingRef.current = false;

      setMessage('');

      return;
    }

    if (isSettled) {
      isAwaitingRef.current = false;

      const noun = resultCount === 1 && !hasMore ? 'result' : 'results';

      setMessage(`${resultCount}${hasMore ? ' or more' : ''} ${noun}`);
    }
  }, [queryKey, isSettled, isError, resultCount, hasMore]);

  return message;
}

/**
 * A polite live region with no visible text. On web it is clipped to a pixel rather than hidden,
 * since a hidden node is not announced. iOS does not read live regions, so it is also spoken.
 */
export function ResultsAnnouncer({
  message,
  testID = 'results-announcer',
}: {
  message: string;
  testID?: string;
}) {
  useEffect(() => {
    if (message && Platform.OS === 'ios') {
      AccessibilityInfo.announceForAccessibility(message);
    }
  }, [message]);

  return (
    <Text
      testID={testID}
      accessibilityLiveRegion="polite"
      {...(isWeb ? { role: 'status' } : {})}
      className="absolute h-px w-px overflow-hidden opacity-0"
      pointerEvents="none">
      {message}
    </Text>
  );
}
